import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle, Star, Zap } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const PricingSection = () => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // monthly | yearly

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/public/subscription-plans`);
      setPlans(res.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const currentLang = i18n.language?.split("-")[0] || "it";

  const getLocalizedField = (plan, field) => {
    if (currentLang === "it") {
      return plan[field] || "";
    }
    return plan[`${field}_${currentLang}`] || plan[field] || "";
  };

  const getLocalizedFeatures = (plan) => {
    if (currentLang === "it") {
      return plan.features || [];
    }
    return plan[`features_${currentLang}`] || plan.features || [];
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat(currentLang === "it" ? "it-IT" : currentLang === "de" ? "de-DE" : currentLang === "es" ? "es-ES" : "en-GB", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getDisplayPrice = (plan) => {
    if (billingPeriod === "yearly" && plan.price_yearly > 0) {
      return plan.price_yearly / 12;
    }
    return plan.price_monthly;
  };

  const getSavingsPercentage = (plan) => {
    if (plan.price_yearly > 0 && plan.price_monthly > 0) {
      const yearlyMonthly = plan.price_yearly / 12;
      const savings = ((plan.price_monthly - yearlyMonthly) / plan.price_monthly) * 100;
      return Math.round(savings);
    }
    return 0;
  };

  const getPricingText = () => {
    const texts = {
      it: {
        title: "Prezzi semplici e trasparenti",
        subtitle: "Scegli il piano più adatto alle tue esigenze. Prova gratis per 14 giorni.",
        monthly: "Mensile",
        yearly: "Annuale",
        perMonth: "/mese",
        free: "Gratis",
        popular: "Più popolare",
        startFree: "Inizia Gratis",
        tryFree: "Prova 14 giorni gratis",
        contactUs: "Contattaci",
        save: "Risparmia",
        vatNote: "Tutti i prezzi sono IVA esclusa. Cancella quando vuoi, senza penali.",
        billedYearly: "fatturato annualmente",
        trialNote: "14 giorni gratis, poi",
        trialDays: "14 giorni",
        afterTrial: "poi"
      },
      en: {
        title: "Simple and transparent pricing",
        subtitle: "Choose the plan that fits your needs. Try free for 14 days.",
        monthly: "Monthly",
        yearly: "Yearly",
        perMonth: "/month",
        free: "Free",
        popular: "Most popular",
        startFree: "Start Free",
        tryFree: "Try 14 days free",
        contactUs: "Contact Us",
        save: "Save",
        vatNote: "All prices exclude VAT. Cancel anytime, no penalties.",
        billedYearly: "billed annually",
        trialNote: "14 days free, then",
        trialDays: "14 days",
        afterTrial: "then"
      },
      de: {
        title: "Einfache und transparente Preise",
        subtitle: "Wählen Sie den Plan, der zu Ihren Bedürfnissen passt. 14 Tage kostenlos testen.",
        monthly: "Monatlich",
        yearly: "Jährlich",
        perMonth: "/Monat",
        free: "Kostenlos",
        popular: "Am beliebtesten",
        startFree: "Kostenlos starten",
        tryFree: "14 Tage kostenlos testen",
        contactUs: "Kontaktieren Sie uns",
        save: "Sparen",
        vatNote: "Alle Preise verstehen sich zzgl. MwSt. Jederzeit kündbar, ohne Strafen.",
        billedYearly: "jährlich abgerechnet",
        trialNote: "14 Tage kostenlos, dann",
        trialDays: "14 Tage",
        afterTrial: "dann"
      },
      es: {
        title: "Precios simples y transparentes",
        subtitle: "Elige el plan que mejor se adapte a tus necesidades. Prueba gratis 14 días.",
        monthly: "Mensual",
        yearly: "Anual",
        perMonth: "/mes",
        free: "Gratis",
        popular: "Más popular",
        startFree: "Empieza Gratis",
        tryFree: "Prueba 14 días gratis",
        contactUs: "Contáctanos",
        save: "Ahorra",
        vatNote: "Todos los precios no incluyen IVA. Cancela cuando quieras, sin penalizaciones.",
        billedYearly: "facturado anualmente",
        trialNote: "14 días gratis, luego",
        trialDays: "14 días",
        afterTrial: "luego"
      }
    };
    return texts[currentLang] || texts.it;
  };

  const text = getPricingText();

  if (loading) {
    return (
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 
            className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight" 
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="pricing-title"
          >
            {text.title}
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            {text.subtitle}
          </p>

          {/* Billing Toggle */}
          <div className="mt-8 inline-flex items-center gap-2 p-1 bg-slate-100 rounded-full">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                billingPeriod === "monthly"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="billing-monthly"
            >
              {text.monthly}
            </button>
            <button
              onClick={() => setBillingPeriod("yearly")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingPeriod === "yearly"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="billing-yearly"
            >
              {text.yearly}
              {plans.some(p => getSavingsPercentage(p) > 0) && (
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-xs">
                  {text.save} 20%
                </Badge>
              )}
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {plans.map((plan, index) => {
            const displayPrice = getDisplayPrice(plan);
            const isFree = displayPrice === 0;
            const isPopular = plan.is_popular;
            const savings = getSavingsPercentage(plan);
            const features = getLocalizedFeatures(plan);

            return (
              <div
                key={plan.plan_id}
                className={`relative rounded-2xl p-8 transition-all duration-300 hover:shadow-xl ${
                  isPopular
                    ? "bg-primary text-white shadow-xl scale-105 z-10"
                    : "bg-white border border-slate-200 hover:border-slate-300"
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`pricing-card-${plan.plan_id}`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-400 text-amber-900 text-xs font-semibold px-3 py-1 shadow-lg">
                      <Star className="h-3 w-3 mr-1 fill-current" />
                      {text.popular.toUpperCase()}
                    </Badge>
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-6">
                  <h3
                    className={`text-xl font-semibold ${isPopular ? "text-white" : "text-slate-900"}`}
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {getLocalizedField(plan, "name")}
                  </h3>
                  <p className={`mt-1 text-sm ${isPopular ? "text-white/80" : "text-slate-500"}`}>
                    {getLocalizedField(plan, "description")}
                  </p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  {plan.plan_id === "plan_starter" ? (
                    // Starter plan: 14 days free trial, then €5/month
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-3xl font-bold ${isPopular ? "text-white" : "text-slate-900"}`}>
                          {text.free}
                        </span>
                        <span className={`text-sm ${isPopular ? "text-white/70" : "text-slate-500"}`}>
                          {text.trialDays || "14 giorni"}
                        </span>
                      </div>
                      <p className={`text-sm mt-1 ${isPopular ? "text-white/80" : "text-slate-600"}`}>
                        {text.afterTrial} <span className="font-semibold">{formatPrice(5)}{text.perMonth}</span>
                      </p>
                    </div>
                  ) : isFree ? (
                    <span className={`text-4xl font-bold ${isPopular ? "text-white" : "text-slate-900"}`}>
                      {text.free}
                    </span>
                  ) : (
                    <>
                      <span className={`text-4xl font-bold ${isPopular ? "text-white" : "text-slate-900"}`}>
                        {formatPrice(displayPrice)}
                      </span>
                      <span className={`${isPopular ? "text-white/80" : "text-slate-500"}`}>
                        {text.perMonth}
                      </span>
                      {billingPeriod === "yearly" && savings > 0 && (
                        <p className={`text-sm mt-1 ${isPopular ? "text-emerald-200" : "text-emerald-600"}`}>
                          {text.billedYearly} ({formatPrice(plan.price_yearly)})
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {features.slice(0, 8).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle
                        className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                          isPopular ? "text-emerald-300" : "text-emerald-500"
                        }`}
                      />
                      <span className={`text-sm ${isPopular ? "text-white" : "text-slate-600"}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                  {features.length > 8 && (
                    <li className={`text-sm pl-7 ${isPopular ? "text-white/70" : "text-slate-500"}`}>
                      +{features.length - 8} {currentLang === "it" ? "altre funzionalità" : currentLang === "en" ? "more features" : currentLang === "de" ? "weitere Funktionen" : "más funciones"}
                    </li>
                  )}
                </ul>

                {/* CTA Button */}
                <Link to="/register" className="block">
                  <Button
                    className={`w-full ${
                      isPopular
                        ? "bg-white text-primary hover:bg-slate-100"
                        : "bg-primary text-white hover:bg-primary/90"
                    }`}
                    size="lg"
                    data-testid={`pricing-cta-${plan.plan_id}`}
                  >
                    {plan.plan_id === "plan_starter" ? (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        {text.tryFree}
                      </>
                    ) : isFree ? (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        {text.startFree}
                      </>
                    ) : plan.plan_id === "plan_business" ? (
                      text.contactUs
                    ) : (
                      text.tryFree
                    )}
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm text-slate-500 mt-12" data-testid="pricing-vat-note">
          {text.vatNote}
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
